import { logger } from "@/utils/logger";
import { isInventoryLoaded, getInventoryCount, getSessionInventory, ensureInventoryLoadedForHydration } from './inventoryUtils';

/**
 * Utility helpers for normalizing and mapping inventory data.
 * Different dealers use slightly different field names (spaces, case, nested structures).
 */
const normalizeVIN = (value: any): string | null => {
  if (!value) return null;
  try {
    return String(value).trim().toUpperCase();
  } catch {
    return null;
  }
};

/**
 * Safely read the first defined value from a list of candidate paths or accessors.
 * Supports:
 *  - direct keys on the object (e.g. "Final Price", "final price")
 *  - accessor functions for nested structures (e.g. d => d.odometer?.value)
 */
const getFirstDefinedValue = (
  data: any,
  candidates: (string | ((data: any) => any))[]
): any => {
  if (!data || typeof data !== 'object') return undefined;

  for (const candidate of candidates) {
    try {
      if (typeof candidate === 'function') {
        const value = candidate(data);
        if (value !== undefined && value !== null) {
          return value;
        }
      } else if (candidate in data) {
        const value = (data as any)[candidate];
        if (value !== undefined && value !== null) {
          return value;
        }
      }
    } catch {
      // Ignore individual candidate failures and continue
    }
  }

  return undefined;
};

/** Detect widget language from the host page meta tag */
const detectLanguage = (): 'fr' | 'en' => {
  try {
    const metaTag = document.querySelector('meta[http-equiv="content-language"]') as HTMLMetaElement;
    if (metaTag && metaTag.content) {
      const langCode = metaTag.content.toLowerCase().split('-')[0];
      if (langCode === 'fr') return 'fr';
    }
  } catch {
    // Ignore errors
  }
  return 'en';
};

/** Normalize an object key: lowercase, strip spaces and underscores */
const normalizeKey = (key: string): string => key.toLowerCase().replace(/[\s_]/g, '');

/**
 * Get full inventory data from in-memory storage
 */
const getInventoryFromStorage = (): any[] => {
  if (!isInventoryLoaded()) {
    logger.log('🔍 No inventory loaded in memory');
    return [];
  }
  
  const inventory = getSessionInventory();
  
  if (!Array.isArray(inventory)) {
    logger.log('⚠️ sessionInventory is not an array');
    return [];
  }
  
  logger.log(`📦 Retrieved ${inventory.length} vehicles from in-memory inventory`);
  return inventory;
};

/**
 * Find vehicle in inventory by VIN
 */
const findVehicleByVIN = (vin: string, inventory: any[]): any | null => {
  const targetVIN = normalizeVIN(vin);
  if (!targetVIN || !Array.isArray(inventory) || inventory.length === 0) {
    return null;
  }

  const vehicle = inventory.find((v: any) => {
    const candidateVIN = normalizeVIN(v?.VIN ?? v?.vin);
    return candidateVIN === targetVIN;
  });

  return vehicle || null;
};

/**
 * Map in-memory inventory vehicle data to AI schema format
 * Handles both legacy ("Main Picture", "Final Price") and newer schemas
 * (e.g. "main picture", "Final price", nested color/odometer objects).
 */
const mapVehicleData = (vehicleData: any): Record<string, any> => {
  const mapped: Record<string, any> = {};

  if (!vehicleData || typeof vehicleData !== 'object') {
    logger.warn('⚠️ HYDRATION: mapVehicleData called with invalid vehicleData', vehicleData);
    return mapped;
  }

  // Picture URL - include image_url from sync-inventory-v2
  const mainPicture = getFirstDefinedValue(vehicleData, [
    'Main Picture',
    'main picture',
    'Main_Picture_URL',
    'main_picture',
    'image_url',
  ]);
  if (mainPicture !== undefined) {
    mapped.Main_Picture_URL = mainPicture;
  }

  // Stock / stock number (include stock_number from sync-inventory-v2)
  const stockNumber = getFirstDefinedValue(vehicleData, [
    'Stock',
    'stock number',
    'Stock_Number',
    'stock_number',
  ]);
  if (stockNumber !== undefined) {
    mapped.Stock_Number = stockNumber;
  }

  // Status
  const status = getFirstDefinedValue(vehicleData, ['Status', 'status']);
  if (status !== undefined) {
    mapped.Status = status;
  }

  // Year / Make / Model / Trim
  const year = getFirstDefinedValue(vehicleData, ['Year', 'year']);
  if (year !== undefined) mapped.Year = year;

  const make = getFirstDefinedValue(vehicleData, ['Make', 'make']);
  if (make !== undefined) mapped.Make = make;

  const model = getFirstDefinedValue(vehicleData, ['Model', 'model']);
  if (model !== undefined) mapped.Model = model;

  const trim = getFirstDefinedValue(vehicleData, ['Trim', 'trim']);
  if (trim !== undefined) mapped.Trim = trim;

  if (detectLanguage() === 'fr') {
    const frenchKey = Object.keys(vehicleData).find(k => normalizeKey(k) === 'trimfrench');
    const frenchTrim = frenchKey ? vehicleData[frenchKey] : undefined;
    if (typeof frenchTrim === 'string' && frenchTrim.trim()) {
      mapped.Trim = frenchTrim;
    }
  }

  // Exterior color - prefer English exterior color (include exterior_color_en from sync-inventory-v2)
  const exteriorColor = getFirstDefinedValue(vehicleData, [
    'Exterior Color',
    'exterior_color_en',
    'exterior_color',
    (d: any) => d.color?.['exterior english'],
    (d: any) => d.color?.exterior,
  ]);
  if (exteriorColor !== undefined) {
    mapped.Exterior_Color = exteriorColor;
  }

  // Price - support multiple naming conventions
  const price = getFirstDefinedValue(vehicleData, [
    'Final Price',
    'Final price',
    'final price',
    'Price',
    'price',
    'finalPrice',
  ]);
  if (price !== undefined) {
    mapped.Price = price;
  }

  // Mileage - direct or via odometer object (include odometer_km from sync-inventory-v2)
  const mileage = getFirstDefinedValue(vehicleData, [
    'Mileage',
    'odometer_km',
    (d: any) => d.odometer?.value,
  ]);
  if (mileage !== undefined) {
    mapped.Mileage = mileage;
  }

  // VDP URL - legacy and new keys (include vdp_url from sync-inventory-v2)
  const vdpUrl = getFirstDefinedValue(vehicleData, [
    'VDP URL',
    'Vehicle Details Page (VDP)',
    'VDP_URL',
    'vdpUrl',
    'vdp_url',
  ]);
  if (vdpUrl !== undefined) {
    mapped.VDP_URL = vdpUrl;
  }

  // Fuel type (include lowercase from sync-inventory-v2)
  const fuelType = getFirstDefinedValue(vehicleData, [
    'Fuel Type',
    'fuel_type',
    'Fuel_Type',
    'fuelType',
  ]);
  if (fuelType !== undefined) {
    mapped.Fuel_Type = fuelType;
  }

  return mapped;
};

/**
 * Hydrate a slim vehicle object with full data from in-memory inventory
 * Works for any dealer that has inventory loaded in memory
 * 
 * @param slimVehicle - Vehicle object with only VIN from AI response
 * @returns Fully hydrated vehicle object or null if VIN not found
 */
export const hydrateVehicleFromInventory = async (slimVehicle: any, preloadedInventory?: any[], retryAttempt: number = 0): Promise<any | null> => {
  // Safety check: must have VIN
  if (!slimVehicle?.VIN) {
    logger.warn('⚠️ HYDRATION: Cannot hydrate vehicle: no VIN provided');
    return slimVehicle; // Return as-is if no VIN
  }
  
  const vin = slimVehicle.VIN;
  logger.log(`🔍 HYDRATION: Attempting to hydrate vehicle with VIN: ${vin} (attempt ${retryAttempt + 1})`);
  
  // Get inventory from in-memory storage (allow reusing preloaded inventory)
  const inventory = Array.isArray(preloadedInventory) ? preloadedInventory : getInventoryFromStorage();
  logger.log(`📦 HYDRATION: Got ${inventory.length} vehicles from storage`);
  
  if (!inventory.length) {
    logger.warn('⚠️ HYDRATION: Cannot hydrate: no inventory loaded in memory');
    logger.warn('⚠️ HYDRATION: Make sure inventory_load=true for this dealer and inventory-index.json exists');
    return slimVehicle; // Return slim version if no inventory available
  }

  // Show sample VINs for debugging (support both VIN and vin)
  const sampleVINs = inventory
    .slice(0, 3)
    .map(v => v.VIN || v.vin)
    .filter(Boolean);
  logger.log(`📋 HYDRATION: Sample VINs in inventory:`, sampleVINs);
  logger.log(`📋 HYDRATION: Looking for VIN: ${vin}`);
  
  // Find vehicle by VIN (case-insensitive, supports VIN / vin)
  const vehicleData = findVehicleByVIN(vin, inventory);
  if (!vehicleData) {
    // If not found and this is the first attempt, wait 2 seconds and retry once
    if (retryAttempt === 0) {
      logger.log('⏳ HYDRATION: VIN not found, waiting 2 seconds before retry...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      return hydrateVehicleFromInventory(slimVehicle, preloadedInventory, 1);
    }
    
    logger.warn(`⚠️ HYDRATION: Vehicle with VIN ${vin} not found in inventory after retry`);
    logger.log(`📋 HYDRATION: Total VINs in inventory: ${inventory.length}`);
    logger.log(
      `📋 HYDRATION: First 5 VINs:`,
      inventory.slice(0, 5).map(v => v.VIN || v.vin)
    );
    logger.warn(`🚫 HYDRATION: Skipping VIN ${vin} because it is not present in inventory index`);
    return null; // Explicitly skip vehicles that are not in the inventory index
  }

  logger.log(`✅ HYDRATION: Found vehicle data in inventory for VIN: ${vin}`);
  logger.log(`📋 HYDRATION: Vehicle data fields:`, Object.keys(vehicleData));
  
  // Map fields from localStorage format to AI schema format
  const mappedData = mapVehicleData(vehicleData);
  logger.log(`🔄 HYDRATION: Mapped ${Object.keys(mappedData).length} fields from inventory`);
  logger.log(`🔄 HYDRATION: Mapped data:`, mappedData);
  
  // Start with inventory data - this is the source of truth
  const hydratedVehicle = { ...mappedData };
  
  // Preserve VIN from AI response (required field)
  hydratedVehicle.VIN = slimVehicle.VIN;
  
  // ONLY override Vehicle_Color if provided in AI response
  // All other fields from AI response (Main_Picture_URL, VDP_URL, etc.) are ignored
  const aiShortColor =
    typeof slimVehicle.Exterior_Color === 'string' && slimVehicle.Exterior_Color.trim()
      ? slimVehicle.Exterior_Color.trim()
      : typeof slimVehicle.Vehicle_Color === 'string' && slimVehicle.Vehicle_Color.trim()
      ? slimVehicle.Vehicle_Color.trim()
      : '';

  if (aiShortColor) {
    hydratedVehicle.Exterior_Color = aiShortColor;
    hydratedVehicle.Vehicle_Color = aiShortColor;
    logger.log(`🎨 HYDRATION: Overriding color with AI value: "${aiShortColor}"`);
  } else if (mappedData.Exterior_Color) {
    logger.log(`🎨 HYDRATION: Using color from inventory: "${mappedData.Exterior_Color}"`);
  }

  logger.log(`✅ HYDRATION: Successfully hydrated vehicle with VIN: ${vin}`);
  logger.log(`✅ HYDRATION: Final hydrated vehicle fields:`, Object.keys(hydratedVehicle));
  
  return hydratedVehicle;
};

/**
 * Process inventory array and hydrate vehicles by VIN
 * 
 * @param inventory - Array of vehicles from AI response
 * @returns Processed inventory array
 */
export const processInventoryHydration = async (inventory: any[]): Promise<any[]> => {
  logger.log('🚀 HYDRATION: processInventoryHydration called', { 
    inventoryLength: inventory?.length,
    isArray: Array.isArray(inventory)
  });
  
  if (!Array.isArray(inventory) || inventory.length === 0) {
    logger.log('⚠️ HYDRATION: No inventory to process');
    return inventory;
  }
  
  logger.log(`💧 HYDRATION: Processing ${inventory.length} vehicles for hydration`);
  logger.log('💧 HYDRATION: Sample vehicle from API:', JSON.stringify(inventory[0]));
  
  // Ensure parallel data loading is complete before hydrating
  // This awaits any in-progress loading that was started during app init
  try {
    const { ensureInventoryReady } = await import('./parallelDataLoader');
    await ensureInventoryReady();
    logger.log('✅ HYDRATION: Parallel inventory loading complete');
  } catch (error) {
    logger.warn('⚠️ HYDRATION: Parallel loader not available, using fallback');
  }
  
  // Fallback: Ensure inventory is loaded in memory (try cache on first use per page load)
  if (!isInventoryLoaded() || getInventoryCount() === 0) {
    logger.log('💾 HYDRATION: Inventory not loaded in memory, attempting synchronous cache load');
    ensureInventoryLoadedForHydration();
  }

  // Polling: Wait up to 3 seconds (retry every 1 sec) for inventory to be ready
  const MAX_RETRIES = 3;
  let inventoryReady = isInventoryLoaded() && getInventoryCount() > 0;
  
  for (let attempt = 0; attempt < MAX_RETRIES && !inventoryReady; attempt++) {
    logger.log(`⏳ HYDRATION: Inventory not ready, waiting 1s... (attempt ${attempt + 1}/${MAX_RETRIES})`);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Try cache load again
    ensureInventoryLoadedForHydration();
    inventoryReady = isInventoryLoaded() && getInventoryCount() > 0;
  }
  
  if (!inventoryReady) {
    logger.warn('⚠️ HYDRATION: Inventory still not ready after 3 seconds, returning unhydrated VIN list');
    // IMPORTANT: Do NOT drop the inventory section entirely; return the original VIN list so UI can still render something.
    return inventory;
  }

  // Check if inventory is loaded in memory
  const inventoryCount = getInventoryCount();
  const fullInventory = getSessionInventory();
  logger.log(`📦 HYDRATION: In-memory inventory count: ${inventoryCount}`);
  logger.log(`📦 HYDRATION: Sample inventory vehicle:`, fullInventory[0] ? JSON.stringify(fullInventory[0]) : 'none');
  
  // Hydrate each vehicle
  const hydratedInventory = await Promise.all(
    inventory.map(async (vehicle, index) => {
      if (!vehicle) {
        logger.log(`⚠️ HYDRATION: Vehicle ${index + 1} is null/undefined, skipping`);
        return null;
      }
      
      // Check if this looks like a slim vehicle (has VIN but missing other fields)
      const isSlimVehicle = vehicle.VIN && !vehicle.Main_Picture_URL;
      
      logger.log(`🔍 HYDRATION: Vehicle ${index + 1}/${inventory.length}:`, {
        hasVIN: !!vehicle.VIN,
        hasPicture: !!vehicle.Main_Picture_URL,
        hasPrice: !!vehicle.Price,
        isSlim: isSlimVehicle,
        vin: vehicle.VIN,
        allFields: Object.keys(vehicle)
      });
      
      if (isSlimVehicle) {
        logger.log(`💧 HYDRATION: Attempting to hydrate vehicle ${index + 1} with VIN: ${vehicle.VIN}`);
        const hydrated = await hydrateVehicleFromInventory(vehicle, fullInventory);
        
        if (!hydrated) {
          logger.log(`🚫 HYDRATION: Skipping vehicle ${index + 1} with VIN ${vehicle.VIN} (not found in inventory)`);
          return null;
        }

        logger.log(`✅ HYDRATION: Result for VIN ${vehicle.VIN}:`, {
          success: !!hydrated.Main_Picture_URL || !!hydrated.Price,
          hasMain_Picture_URL: !!hydrated.Main_Picture_URL,
          hasPrice: !!hydrated.Price,
          hasMake: !!hydrated.Make,
          allFields: Object.keys(hydrated)
        });
        return hydrated;
      }
      
      logger.log(`⏭️ HYDRATION: Skipping vehicle ${index + 1} (already has full data)`);
      return vehicle; // Already has full data
    })
  );
  
  const filteredInventory = hydratedInventory.filter(Boolean) as any[]; // Remove null entries to avoid rendering empty cards
  
  logger.log('✅ HYDRATION: Processing complete', {
    originalCount: inventory.length,
    hydratedCount: hydratedInventory.length,
    filteredCount: filteredInventory.length,
    removedNulls: hydratedInventory.length - filteredInventory.length
  });
  
  return filteredInventory; // Return filtered array (without nulls)
};
